/**
 * projectUtils - 项目工具函数集合
 * 使用 v2 类型 (IProject, ITrigger, IAction 等)
 */

import { Log } from "@fanfanlo";
import { InjectKeyType } from "@app/static";
import { IProject, IPermissionData, getActionUtils, getTriggerUtils } from '@droid/project-v2-interface';

const fileLog = new Log(false, 'project-utils_f')
fileLog.pause = true;
fileLog.childrenPaused = true;

/**
 * 获取Project权限列表
 * @param project 项目数据
 * @returns 权限名称列表
 */
function getProjectPermissions(project: IProject): string[] {
    const fnLog = fileLog.sub(false, 'getProjectPermissions_fn')
    fnLog.pause = true;
    fnLog.log('[getProjectPermissions] 开始获取权限, projectUlid:', project.ulid);

    const permissions = new Set<string>();

    if (!project.children || project.children.length === 0) {
        fnLog.log('[getProjectPermissions] project.children 不存在或为空');
        return [];
    }
    fnLog.log('[getProjectPermissions] project.children.length:', project.children.length);

    // 循环所有 triggerActionGroup
    for (let g = 0; g < project.children.length; g++) {
        const triggerActionGroup = project.children[g];
        fnLog.log('[getProjectPermissions] 处理 triggerActionGroup[', g, '], ulid:', triggerActionGroup.ulid);

        const triggerActionChildren = triggerActionGroup.children;
        if (!triggerActionChildren) {
            fnLog.log('[getProjectPermissions] triggerActionGroup[', g, '].children 不存在');
            continue;
        }

        for (let i = 0; i < triggerActionChildren.length; i++) {
            const triggerAction = triggerActionChildren[i];
            fnLog.log('[getProjectPermissions] 处理triggerAction[', i, ']');

            const triggerGroup = triggerAction.triggerGroup;
            if (triggerGroup) {
                const triggerChildren = triggerGroup.children;
                if (triggerChildren) {
                    for (let j = 0; j < triggerChildren.length; j++) {
                        const trigger = triggerChildren[j];
                        if (trigger.permissions) {
                            fnLog.log('[getProjectPermissions] trigger有权限, 数量:', trigger.permissions.length);
                            for (let k = 0; k < trigger.permissions.length; k++) {
                                const permission = trigger.permissions[k];
                                fnLog.log('[getProjectPermissions] 添加权限:', permission.name);
                                permissions.add(permission.name);
                            }
                        }
                    }
                }
            }

            const actionGroup = triggerAction.actionGroup;
            if (actionGroup) {
                const actionChildren = actionGroup.children;
                if (actionChildren) {
                    for (let j = 0; j < actionChildren.length; j++) {
                        const action = actionChildren[j];
                        if (action.permissions) {
                            fnLog.log('[getProjectPermissions] action有权限, 数量:', action.permissions.length);
                            for (let k = 0; k < action.permissions.length; k++) {
                                const permission = action.permissions[k];
                                fnLog.log('[getProjectPermissions] 添加权限:', permission.name);
                                permissions.add(permission.name);
                            }
                        }
                    }
                }
            }
        }
    }

    const result = [...permissions];
    fnLog.log('[getProjectPermissions] 权限获取完成, 总数:', result.length);
    return result;
}

/**
 * 获取Project权限详细信息列表
 * 返回包含name、value、reason的完整权限数据
 * 异步加载国际化文本 (通过各 action/trigger 的 loadI18nTexts)
 */
async function getProjectPermissionsData(project: IProject): Promise<IPermissionData[]> {
    const fnLog = fileLog.sub(false, 'getProjectPermissionsData_fn')
    fnLog.pause = true;
    fnLog.log('[getProjectPermissionsData] 开始获取权限详细信息, projectUlid:', project.ulid);

    if (!project.children || project.children.length === 0) {
        fnLog.log('[getProjectPermissionsData] project.children 不存在或为空');
        return [];
    }
    fnLog.log('[getProjectPermissionsData] project.children.length:', project.children.length);

    // 收集权限数据
    const permissionsMap = new Map<string, IPermissionData>();

    // 循环所有 triggerActionGroup
    for (let g = 0; g < project.children.length; g++) {
        const triggerActionGroup = project.children[g];
        fnLog.log('[getProjectPermissionsData] 处理 triggerActionGroup[', g, '], ulid:', triggerActionGroup.ulid);

        const triggerActionChildren = triggerActionGroup.children;
        if (!triggerActionChildren) {
            fnLog.log('[getProjectPermissionsData] triggerActionGroup[', g, '].children 不存在');
            continue;
        }

        // 先调用各 action/trigger 的 loadI18nTexts 加载国际化文本
        fnLog.log('[getProjectPermissionsData] 开始加载国际化文本');

        for (let i = 0; i < triggerActionChildren.length; i++) {
            const triggerAction = triggerActionChildren[i];
            fnLog.log('[getProjectPermissionsData] 处理triggerAction[', i, ']');

            // 加载 trigger 的 i18n
            const triggerGroup = triggerAction.triggerGroup;
            if (triggerGroup) {
                const triggerChildren = triggerGroup.children;
                if (triggerChildren) {
                    for (let j = 0; j < triggerChildren.length; j++) {
                        const trigger = triggerChildren[j];
                        fnLog.log('[getProjectPermissionsData] 处理trigger[', j, '], type:', trigger.type);

                        const triggerUtils = getTriggerUtils(trigger.type);
                        fnLog.log('[getProjectPermissionsData] triggerUtils 是否存在:', !!triggerUtils);

                        if (triggerUtils) {
                            const hasLoadI18nTexts = !!triggerUtils.loadI18nTexts;
                            fnLog.log('[getProjectPermissionsData] triggerUtils.loadI18nTexts 是否存在:', hasLoadI18nTexts);

                            if (hasLoadI18nTexts) {
                                fnLog.log('[getProjectPermissionsData] 调用 triggerUtils.loadI18nTexts');
                                await triggerUtils.loadI18nTexts(trigger);
                                fnLog.log('[getProjectPermissionsData] trigger i18n 加载完成');
                            }
                        }
                    }
                }
            }

            // 加载 action 的 i18n
            const actionGroup = triggerAction.actionGroup;
            if (actionGroup) {
                const actionChildren = actionGroup.children;
                if (actionChildren) {
                    for (let j = 0; j < actionChildren.length; j++) {
                        const action = actionChildren[j];
                        fnLog.log('[getProjectPermissionsData] 处理action[', j, '], type:', action.type);

                        const actionUtils = getActionUtils(action.type);
                        fnLog.log('[getProjectPermissionsData] actionUtils 是否存在:', !!actionUtils);

                        if (actionUtils) {
                            const hasLoadI18nTexts = !!actionUtils.loadI18nTexts;
                            fnLog.log('[getProjectPermissionsData] actionUtils.loadI18nTexts 是否存在:', hasLoadI18nTexts);

                            if (hasLoadI18nTexts) {
                                fnLog.log('[getProjectPermissionsData] 调用 actionUtils.loadI18nTexts');
                                await actionUtils.loadI18nTexts(action);
                                fnLog.log('[getProjectPermissionsData] action i18n 加载完成');
                            }
                        }
                    }
                }
            }
        }

        fnLog.log('[getProjectPermissionsData] triggerActionGroup[', g, '] i18n 加载完成, 开始收集权限');

        for (let i = 0; i < triggerActionChildren.length; i++) {
            const triggerAction = triggerActionChildren[i];
            fnLog.log('[getProjectPermissionsData] 收集triggerAction[', i, ']的权限');

            const triggerGroup = triggerAction.triggerGroup;
            if (triggerGroup) {
                const triggerChildren = triggerGroup.children;
                if (triggerChildren) {
                    for (let j = 0; j < triggerChildren.length; j++) {
                        const trigger = triggerChildren[j];
                        if (trigger.permissions) {
                            fnLog.log('[getProjectPermissionsData] trigger有权限, 数量:', trigger.permissions.length);
                            for (let k = 0; k < trigger.permissions.length; k++) {
                                const permission = trigger.permissions[k];
                                fnLog.log('[getProjectPermissionsData] 添加权限:', permission.name, 'value:', permission.value);
                                if (permission.value) {
                                    permissionsMap.set(permission.value, permission);
                                }
                            }
                        }
                    }
                }
            }

            const actionGroup = triggerAction.actionGroup;
            if (actionGroup) {
                const actionChildren = actionGroup.children;
                if (actionChildren) {
                    for (let j = 0; j < actionChildren.length; j++) {
                        const action = actionChildren[j];
                        if (action.permissions) {
                            fnLog.log('[getProjectPermissionsData] action有权限, 数量:', action.permissions.length);
                            for (let k = 0; k < action.permissions.length; k++) {
                                const permission = action.permissions[k];
                                fnLog.log('[getProjectPermissionsData] 添加权限:', permission.name, 'value:', permission.value);
                                if (permission.value) {
                                    permissionsMap.set(permission.value, permission);
                                }
                            }
                        }
                    }
                }
            }
        }
    }

    const result = Array.from(permissionsMap.values());
    fnLog.log('[getProjectPermissionsData] 权限详细信息获取完成, 总数:', result.length, JSON.stringify(result));
    return result;
}

/**
 * 获取Project注入配置列表
 * 收集所有 action 和 trigger 中声明的 injects
 * 返回混合格式数组,支持 InjectKeyType 和 [InjectKeyType, string]
 */
function getProjectInjects(project: IProject): (InjectKeyType | [InjectKeyType, string])[] {
    const fnLog = fileLog.sub(false, 'getProjectInjects_fn')
    fnLog.pause = false;
    fnLog.log('[getProjectInjects] 开始获取注入配置, projectUlid:', project.ulid);

    const injectsArray: (InjectKeyType | [InjectKeyType, string])[] = [];
    const injectKeysSet = new Set<string>();

    if (!project.children || project.children.length === 0) {
        fnLog.log('[getProjectInjects] project.children 不存在或为空');
        return [];
    }
    fnLog.log('[getProjectInjects] project.children.length:', project.children.length);

    // 循环所有 triggerActionGroup
    for (let g = 0; g < project.children.length; g++) {
        const triggerActionGroup = project.children[g];
        fnLog.log('[getProjectInjects] 处理 triggerActionGroup[', g, '], ulid:', triggerActionGroup.ulid);

        const triggerActionChildren = triggerActionGroup.children;
        if (!triggerActionChildren) {
            fnLog.log('[getProjectInjects] triggerActionGroup[', g, '].children 不存在');
            continue;
        }

        fnLog.log('[getProjectInjects] triggerActionChildren长度:', triggerActionChildren.length);

        for (let i = 0; i < triggerActionChildren.length; i++) {
            const triggerAction = triggerActionChildren[i];
            fnLog.log('[getProjectInjects] 处理triggerAction[', i, ']');

            const triggerGroup = triggerAction.triggerGroup;
            if (triggerGroup) {
                const triggerChildren = triggerGroup.children;
                if (triggerChildren) {
                    fnLog.log('[getProjectInjects] triggerChildren长度:', triggerChildren.length);
                    for (let j = 0; j < triggerChildren.length; j++) {
                        const trigger = triggerChildren[j];
                        fnLog.log('[getProjectInjects] 处理trigger[', j, ']');
                        if (trigger.injects) {
                            fnLog.log('[getProjectInjects] trigger有injects, 数量:', trigger.injects.length);
                            for (let k = 0; k < trigger.injects.length; k++) {
                                const injectConfig = trigger.injects[k];
                                fnLog.log('[getProjectInjects] 处理inject[', k, ']:', JSON.stringify(injectConfig));

                                let injectKey: string;
                                let varName: string | undefined;

                                // 兼容两种格式
                                const isArray = Array.isArray(injectConfig);
                                fnLog.log('[getProjectInjects] injectConfig是否为数组:', isArray);

                                if (isArray) {
                                    // 数组格式: [key, varName]
                                    injectKey = injectConfig[0];
                                    varName = injectConfig[1];
                                    fnLog.log('[getProjectInjects] 数组格式, key:', injectKey, ', varName:', varName);
                                } else {
                                    // 对象格式: { key, varName }
                                    injectKey = injectConfig.key;
                                    varName = injectConfig.varName;
                                    fnLog.log('[getProjectInjects] 对象格式, key:', injectKey, ', varName:', varName);
                                }

                                const hasKey = injectKeysSet.has(injectKey);
                                fnLog.log('[getProjectInjects] 是否已存在:', hasKey);

                                if (!hasKey) {
                                    if (varName) {
                                        fnLog.log('[getProjectInjects] 添加新inject(数组格式)');
                                        injectsArray.push([injectKey as InjectKeyType, varName]);
                                    } else {
                                        fnLog.log('[getProjectInjects] 添加新inject(字符串格式)');
                                        injectsArray.push(injectKey as InjectKeyType);
                                    }
                                    injectKeysSet.add(injectKey);
                                }
                            }
                        }
                    }
                }
            }

            const actionGroup = triggerAction.actionGroup;
            if (actionGroup) {
                const actionChildren = actionGroup.children;
                if (actionChildren) {
                    fnLog.log('[getProjectInjects] actionChildren长度:', actionChildren.length);
                    for (let j = 0; j < actionChildren.length; j++) {
                        const action = actionChildren[j];
                        fnLog.log('[getProjectInjects] 处理action[', j, ']');
                        if (action.injects) {
                            fnLog.log('[getProjectInjects] action有injects, 数量:', action.injects.length);
                            for (let k = 0; k < action.injects.length; k++) {
                                const injectConfig = action.injects[k];
                                fnLog.log('[getProjectInjects] 处理inject[', k, ']:', JSON.stringify(injectConfig));

                                let injectKey: string;
                                let varName: string | undefined;

                                // 兼容两种格式
                                const isArray = Array.isArray(injectConfig);
                                fnLog.log('[getProjectInjects] injectConfig是否为数组:', isArray);

                                if (isArray) {
                                    // 数组格式: [key, varName]
                                    injectKey = injectConfig[0];
                                    varName = injectConfig[1];
                                    fnLog.log('[getProjectInjects] 数组格式, key:', injectKey, ', varName:', varName);
                                } else {
                                    // 对象格式: { key, varName }
                                    injectKey = injectConfig.key;
                                    varName = injectConfig.varName;
                                    fnLog.log('[getProjectInjects] 对象格式, key:', injectKey, ', varName:', varName);
                                }

                                const hasKey = injectKeysSet.has(injectKey);
                                fnLog.log('[getProjectInjects] 是否已存在:', hasKey);

                                if (!hasKey) {
                                    if (varName) {
                                        fnLog.log('[getProjectInjects] 添加新inject(数组格式)');
                                        injectsArray.push([injectKey as InjectKeyType, varName]);
                                    } else {
                                        fnLog.log('[getProjectInjects] 添加新inject(字符串格式)');
                                        injectsArray.push(injectKey as InjectKeyType);
                                    }
                                    injectKeysSet.add(injectKey);
                                }
                            }
                        }
                    }
                }
            }
        }
    }

    fnLog.log('[getProjectInjects] 注入配置获取完成, 总数:', injectsArray.length);
    return injectsArray;
}

/**
 * 清理项目中的空 TriggerAction
 * 空 TriggerAction 定义: triggerGroup.children 和 actionGroup.children 都为空
 * @param project 项目数据
 * @returns 被清理的空 TriggerAction 数量
 */
function cleanEmptyTriggerActions(project: IProject): number {
    const fnLog = fileLog.sub(false, 'cleanEmptyTriggerActions_fn')
    fnLog.pause = true;
    fnLog.log('[cleanEmptyTriggerActions] 开始清理空 TriggerAction')

    if (!project.children || project.children.length === 0) {
        fnLog.log('[cleanEmptyTriggerActions] project.children 不存在或为空,跳过清理')
        return 0
    }
    fnLog.log('[cleanEmptyTriggerActions] project.children.length:', project.children.length);

    let totalRemovedCount = 0;

    // 循环所有 triggerActionGroup
    for (let g = 0; g < project.children.length; g++) {
        const triggerActionGroup = project.children[g];
        fnLog.log('[cleanEmptyTriggerActions] 处理 triggerActionGroup[', g, '], ulid:', triggerActionGroup.ulid);

        const triggerActionChildren = triggerActionGroup.children;
        if (!triggerActionChildren) {
            fnLog.log('[cleanEmptyTriggerActions] triggerActionGroup[', g, '].children 不存在,跳过');
            continue;
        }

        // 记录清理前的数量
        const originalCount = triggerActionChildren.length
        fnLog.log('[cleanEmptyTriggerActions] 清理前 TriggerAction 数量:', originalCount)

        // 过滤空的 triggerAction
        fnLog.log('[cleanEmptyTriggerActions] 开始过滤')
        const filteredList = triggerActionChildren.filter((triggerAction, index) => {
            fnLog.log('[cleanEmptyTriggerActions] 检查 triggerAction[', index, '] ulid:', triggerAction.ulid)

            // 检查 triggerGroup
            const triggerGroup = triggerAction.triggerGroup;
            const hasTriggerGroup = triggerGroup ? true : false
            fnLog.log('[cleanEmptyTriggerActions] triggerAction[', index, '] triggerGroup 是否存在:', hasTriggerGroup)

            const triggerCount = triggerGroup?.children?.length || 0
            fnLog.log('[cleanEmptyTriggerActions] triggerAction[', index, '] trigger 数量:', triggerCount)

            // 检查 actionGroup
            const actionGroup = triggerAction.actionGroup;
            const hasActionGroup = actionGroup ? true : false
            fnLog.log('[cleanEmptyTriggerActions] triggerAction[', index, '] actionGroup 是否存在:', hasActionGroup)

            const actionCount = actionGroup?.children?.length || 0
            fnLog.log('[cleanEmptyTriggerActions] triggerAction[', index, '] action 数量:', actionCount)

            // 判断是否为空(trigger 和 action 都为空)
            const hasTriggers = triggerCount > 0
            const hasActions = actionCount > 0

            fnLog.log('[cleanEmptyTriggerActions] triggerAction[', index, '] hasTriggers:', hasTriggers)
            fnLog.log('[cleanEmptyTriggerActions] triggerAction[', index, '] hasActions:', hasActions)

            // 至少要有 trigger 或 action 之一
            const shouldKeep = hasTriggers || hasActions
            fnLog.log('[cleanEmptyTriggerActions] triggerAction[', index, '] shouldKeep:', shouldKeep)

            if (!shouldKeep) {
                fnLog.log('[cleanEmptyTriggerActions] 清理空的 triggerAction[', index, '] ulid:', triggerAction.ulid)
            } else {
                fnLog.log('[cleanEmptyTriggerActions] 保留 triggerAction[', index, '] ulid:', triggerAction.ulid)
            }

            return shouldKeep
        })

        // 更新列表
        fnLog.log('[cleanEmptyTriggerActions] 更新 children')
        triggerActionGroup.children = filteredList
        fnLog.log('[cleanEmptyTriggerActions] children 已更新，长度:', filteredList.length)

        const removedCount = originalCount - filteredList.length
        totalRemovedCount += removedCount
        fnLog.log('[cleanEmptyTriggerActions] triggerActionGroup[', g, '] 清理了', removedCount, '个空 TriggerAction')
    }

    fnLog.log('[cleanEmptyTriggerActions] 总共清理了', totalRemovedCount, '个空 TriggerAction')
    return totalRemovedCount
}

// ==================== 导出 ====================

export const projectUtils = {
    getProjectPermissions,
    getProjectPermissionsData,
    getProjectInjects,
    cleanEmptyTriggerActions
};
